import { useCallback, useEffect, useState } from 'react';

export function useTabParam<T extends string>(
  param: string,
  valid: readonly T[],
  def: T,
  normalizeOnMount = true,
): [T, (v: T) => void] {
  const read = useCallback((): T => {
    const raw = new URLSearchParams(window.location.search).get(param);
    return raw && (valid as readonly string[]).includes(raw) ? (raw as T) : def;
  }, [param, valid, def]);

  const [tab, setTabState] = useState<T>(read);

  useEffect(() => {
    const onPop = () => setTabState(read());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [read]);

  // Normalizace na mount: i výchozí tab se promítne do URL (?tab=...), ať má
  // KAŽDÝ tab viditelnou URL. replaceState (bez nové položky v historii).
  // Vypnuto pro vnořené sub-taby (jinak by se v URL hromadily stale parametry).
  useEffect(() => {
    if (!normalizeOnMount) return;
    const sp = new URLSearchParams(window.location.search);
    if (!sp.get(param)) {
      sp.set(param, tab);
      window.history.replaceState(null, '', window.location.pathname + `?${sp.toString()}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setTab = useCallback(
    (v: T) => {
      const sp = new URLSearchParams(window.location.search);
      sp.set(param, v);
      window.history.pushState(null, '', window.location.pathname + `?${sp.toString()}`);
      setTabState(v);
    },
    [param],
  );

  return [tab, setTab];
}

// Varianta s mapováním stav→URL-slug. Použij, když stav tabu není čistý
// URL-slug (např. detail uživatele má stav = český label „Přehled", ale v URL
// chceme ?tab=prehled). `toSlug` mapuje hodnotu stavu na URL slug.
export function useTabParamMapped<T extends string>(
  param: string,
  toSlug: Record<T, string>,
  def: T,
  normalizeOnMount = true,
): [T, (v: T) => void] {
  const read = useCallback((): T => {
    const raw = new URLSearchParams(window.location.search).get(param);
    if (!raw) return def;
    const hit = (Object.keys(toSlug) as T[]).find((k) => toSlug[k] === raw);
    return hit ?? def;
  }, [param, toSlug, def]);

  const [tab, setTabState] = useState<T>(read);

  useEffect(() => {
    const onPop = () => setTabState(read());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [read]);

  useEffect(() => {
    if (!normalizeOnMount) return;
    const sp = new URLSearchParams(window.location.search);
    if (!sp.get(param)) {
      sp.set(param, toSlug[tab]);
      window.history.replaceState(null, '', window.location.pathname + `?${sp.toString()}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setTab = useCallback(
    (v: T) => {
      const sp = new URLSearchParams(window.location.search);
      sp.set(param, toSlug[v]);
      window.history.pushState(null, '', window.location.pathname + `?${sp.toString()}`);
      setTabState(v);
    },
    [param, toSlug],
  );

  return [tab, setTab];
}
